/**
 * A foto da carteirinha em tamanho grande, numa caixa no centro da tela.
 *
 * Papel: mostra a foto gravada de uma apólice com o nome do arquivo e o dia da
 * última troca; o clique na imagem copia a foto, "Baixar foto" a salva com o
 * nome de `foto.nome`, e "Alterar foto" escolhe uma nova, mostra a prévia e só
 * grava no "Salvar foto".
 *
 * Como usar: quem abre é a `TabelaPlanos`, pelo clique na miniatura, montando
 * o modal só enquanto ele está aberto e com `key` no `id` da apólice. É a
 * desmontagem no fechamento que descarta a foto escolhida e ainda não salva —
 * um modal mantido montado e só escondido reabriria com a troca pendente de
 * outra vez. `podeAlterar` deverá vir do nível `editar` em saúde: sem ele o
 * botão "Alterar foto" não aparece.
 *
 * Depois do "Salvar foto", a apólice que o `PUT` devolveu vale até a lista de
 * apólices voltar com a mesma foto — das duas, vale a de `foto.atualizadaEm`
 * mais recente —, para o modal não mostrar a foto antiga no intervalo entre a
 * resposta do envio e a da lista.
 *
 * Copiar só vale para a foto gravada: com a nova escolhida e ainda não salva,
 * o clique na imagem fica desabilitado, porque copiar a prévia faria parecer
 * que ela já está gravada.
 */
import {
  diaNoEscritorio,
  nomeDoArquivoDaCarteirinha,
  tipoDaCarteirinha,
  type ExtensaoDaFotoDaCarteirinha,
} from '@concierge/shared';
import { type ChangeEvent, type ReactNode, useEffect, useRef, useState } from 'react';

import { ErroDaApi } from '@/shared/api';
import { formatarData } from '@/shared/lib/formato';
import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  type PlanoComFoto,
  copiarFotoComoPng,
  lerFotoDaCarteirinha,
  temFoto,
} from '../foto-da-carteirinha';
import {
  useEnviarFotoDaCarteirinha,
  useFotoDaCarteirinha,
} from '../hooks/usar-foto-da-carteirinha';

/** Por quanto tempo "Foto copiada." fica à vista. */
const DURACAO_DO_AVISO_EM_MS = 2000;

/** O tipo da carteirinha como o título o escreve. */
const TIPO_ESCRITO = { MEDICINA: 'Medicina', ODONTOLOGIA: 'Odontologia' } as const;

/** A área da imagem enquanto ela não está pronta: a proporção da miniatura, 8:5. */
const CLASSES_DA_AREA_VAZIA =
  'flex aspect-[8/5] max-h-[60vh] w-full items-center justify-center rounded-md border border-linha-forte';

/** A etiqueta mono no canto inferior direito da imagem. */
const CLASSES_DA_ETIQUETA =
  'pointer-events-none absolute right-2 bottom-2 rounded-sm border border-linha-forte bg-superficie px-1.5 py-0.5 font-mono text-[10.5px]';

/** O que o modal está mostrando: a foto gravada ou a escolhida e ainda não salva. */
type Modo =
  | { tipo: 'ver' }
  | {
      tipo: 'alterando';
      /** A imagem escolhida, já validada por `novaFotoDaCarteirinhaSchema`. */
      dataUrl: string;
      extensao: ExtensaoDaFotoDaCarteirinha;
      /** Quando a imagem foi escolhida, de onde sai o dia do nome do arquivo. */
      instante: Date;
    };

/** O resultado da última cópia, enquanto ele está à vista. */
type ResultadoDaCopia = 'copiada' | 'falhou' | null;

interface ModalDaCarteirinhaProps {
  /** A apólice como a lista a traz, com a foto gravada. */
  plano: PlanoComFoto;
  /** Verdadeiro para quem tem `editar` em saúde, e só então aparece "Alterar foto". */
  podeAlterar: boolean;
  /** Chamado pelo `Esc` e pelo clique no véu; quem chama desmonta o modal. */
  onFechar: () => void;
}

/**
 * A mensagem de uma falha do envio, como ela aparece no modal.
 *
 * @param erro - O que `mutateAsync` rejeitou.
 * @returns A mensagem do servidor — a de `campos.foto`, quando a API apontou a
 *   imagem, e a geral no resto, inclusive a do 503 —, ou a da falha de rede.
 */
function mensagemDaFalhaDoEnvio(erro: unknown): string {
  if (erro instanceof ErroDaApi) return erro.campos?.foto?.[0] ?? erro.message;
  return 'O envio falhou antes de chegar ao servidor.';
}

/**
 * O modal da carteirinha.
 *
 * @param plano - A apólice da linha clicada, sempre com foto.
 * @param podeAlterar - Verdadeiro quando o nível em saúde é `editar`.
 * @param onFechar - Chamado quando a caixa pede para fechar.
 * @returns A caixa aberta, com a imagem, o nome do arquivo e os botões.
 */
export function ModalDaCarteirinha({ plano, podeAlterar, onFechar }: ModalDaCarteirinhaProps) {
  const [modo, setModo] = useState<Modo>({ tipo: 'ver' });
  const [salvo, setSalvo] = useState<PlanoComFoto | null>(null);
  const [copia, setCopia] = useState<ResultadoDaCopia>(null);
  const [erroDoArquivo, setErroDoArquivo] = useState<string | null>(null);
  const [erroDoEnvio, setErroDoEnvio] = useState<string | null>(null);
  const seletor = useRef<HTMLInputElement>(null);
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);

  const atual =
    salvo !== null && salvo.foto.atualizadaEm > plano.foto.atualizadaEm ? salvo : plano;
  const imagemGravada = useFotoDaCarteirinha(atual);
  const enviar = useEnviarFotoDaCarteirinha();
  const enviando = enviar.isPending;
  const alterando = modo.tipo === 'alterando';

  useEffect(
    () => () => {
      if (temporizador.current !== null) clearTimeout(temporizador.current);
    },
    [],
  );

  /** Apaga o resultado da cópia anterior e o temporizador que o esconderia. */
  function limparCopia() {
    if (temporizador.current !== null) clearTimeout(temporizador.current);
    temporizador.current = null;
    setCopia(null);
  }

  /** Fecha a caixa pelo `Esc` ou pelo véu, descartando a foto não salva. */
  function handleOpenChange(aberto: boolean) {
    if (!aberto) onFechar();
  }

  /**
   * Copia a foto gravada. A chamada a `copiarFotoComoPng` sai sem `await`
   * antes, ainda dentro do clique, como o Safari exige.
   */
  function handleCopiar() {
    const imagem = imagemGravada.blob;
    if (imagem === null || alterando) return;
    limparCopia();
    copiarFotoComoPng(imagem).then(
      () => {
        setCopia('copiada');
        temporizador.current = setTimeout(() => {
          temporizador.current = null;
          setCopia(null);
        }, DURACAO_DO_AVISO_EM_MS);
      },
      () => setCopia('falhou'),
    );
  }

  /** Abre o seletor de arquivo. */
  function handleAlterar() {
    seletor.current?.click();
  }

  /** Lê o arquivo escolhido e, se passar no schema, mostra-o como foto nova. */
  function handleEscolher(evento: ChangeEvent<HTMLInputElement>) {
    const arquivo = evento.currentTarget.files?.[0];
    evento.currentTarget.value = '';
    if (arquivo === undefined) return;

    void lerFotoDaCarteirinha(arquivo).then((leitura) => {
      if (!leitura.ok) {
        setErroDoArquivo(leitura.mensagem);
        return;
      }
      limparCopia();
      setErroDoArquivo(null);
      setErroDoEnvio(null);
      setModo({
        tipo: 'alterando',
        dataUrl: leitura.dataUrl,
        extensao: leitura.extensao,
        instante: new Date(),
      });
    });
  }

  /** Descarta a foto escolhida e volta à gravada. */
  function handleCancelar() {
    setErroDoArquivo(null);
    setErroDoEnvio(null);
    setModo({ tipo: 'ver' });
  }

  /** Envia a foto escolhida e, se a API aceitar, volta a mostrar a gravada. */
  async function handleSalvar() {
    if (modo.tipo !== 'alterando') return;
    setErroDoEnvio(null);

    try {
      const gravado = await enviar.mutateAsync({
        id: atual.id,
        dados: { foto: modo.dataUrl },
      });
      if (temFoto(gravado)) setSalvo(gravado);
      setModo({ tipo: 'ver' });
    } catch (erro) {
      setErroDoEnvio(mensagemDaFalhaDoEnvio(erro));
    }
  }

  const apelido =
    atual.pessoa.apelido === '' ? atual.pessoa.nomeCompleto : atual.pessoa.apelido;
  const tipo = TIPO_ESCRITO[tipoDaCarteirinha(atual.plano)];
  const nomeMostrado = alterando
    ? nomeDoArquivoDaCarteirinha({
        nomeCompleto: atual.pessoa.nomeCompleto,
        plano: atual.plano,
        instante: modo.instante,
        extensao: modo.extensao,
      })
    : atual.foto.nome;
  const urlDaImagem = alterando ? modo.dataUrl : imagemGravada.url;

  let imagem: ReactNode;
  if (!alterando && imagemGravada.falhou) {
    imagem = (
      <div className={cn(CLASSES_DA_AREA_VAZIA, 'text-sm text-texto-fraco')}>
        Não foi possível carregar a foto.
      </div>
    );
  } else if (urlDaImagem === null) {
    imagem = (
      <div
        role="status"
        aria-label="Carregando a foto"
        className={cn(CLASSES_DA_AREA_VAZIA, 'border-dashed')}
      />
    );
  } else {
    imagem = (
      <button
        type="button"
        aria-label="Copiar a foto da carteirinha"
        disabled={alterando}
        onClick={handleCopiar}
        className="group relative block w-full cursor-copy overflow-hidden rounded-md border border-linha-forte bg-superficie disabled:cursor-default"
      >
        <img src={urlDaImagem} alt="" className="block max-h-[60vh] w-full object-contain" />
        {alterando ? (
          <span className={cn(CLASSES_DA_ETIQUETA, 'text-aviso')}>
            Nova foto — ainda não salva
          </span>
        ) : (
          <span
            className={cn(
              CLASSES_DA_ETIQUETA,
              'text-texto-medio opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100',
            )}
          >
            Clique para copiar
          </span>
        )}
      </button>
    );
  }

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="w-[42rem]">
        <DialogHeader>
          <DialogTitle>
            Carteirinha — {apelido} · {tipo}
          </DialogTitle>
          <DialogDescription className="font-mono text-[11px] text-texto-fraco">
            {atual.operadora} — {atual.plano}
          </DialogDescription>
        </DialogHeader>

        <input
          ref={seletor}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          aria-label="Escolher a nova foto da carteirinha"
          className="hidden"
          disabled={enviando}
          onChange={handleEscolher}
        />

        {imagem}

        <div className="mt-2 flex flex-col gap-0.5 font-mono text-[11px] text-texto-fraco">
          <p className="flex flex-wrap gap-x-3">
            <span>{nomeMostrado}</span>
            <span
              role="status"
              className={copia === 'falhou' ? 'text-perigo' : 'text-sucesso'}
            >
              {copia === 'copiada' && 'Foto copiada.'}
              {copia === 'falhou' && 'Não foi possível copiar a foto neste navegador.'}
            </span>
          </p>
          {!alterando && (
            <p>
              Atualizada em {formatarData(diaNoEscritorio(new Date(atual.foto.atualizadaEm)))}
            </p>
          )}
        </div>

        {erroDoArquivo !== null && (
          <p className="mt-1.5 text-sm text-destructive" role="alert">
            {erroDoArquivo}
          </p>
        )}
        {erroDoEnvio !== null && (
          <p className="mt-1.5 text-sm text-destructive" role="alert">
            {erroDoEnvio}
          </p>
        )}

        <DialogFooter>
          {alterando ? (
            <>
              <Button
                type="button"
                variant="ghost"
                disabled={enviando}
                onClick={handleCancelar}
              >
                Cancelar
              </Button>
              <Button type="button" disabled={enviando} onClick={() => void handleSalvar()}>
                {enviando ? 'Salvando…' : 'Salvar foto'}
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="secondary" onClick={onFechar}>
                Fechar
              </Button>
              {imagemGravada.url !== null ? (
                <Button asChild variant="secondary">
                  <a href={imagemGravada.url} download={atual.foto.nome}>
                    Baixar foto
                  </a>
                </Button>
              ) : (
                <Button type="button" variant="secondary" disabled>
                  Baixar foto
                </Button>
              )}
              {podeAlterar && (
                <Button type="button" onClick={handleAlterar}>
                  Alterar foto
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
